import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../hooks/useDeviceQuery.hook';
import { Dash } from '../BlankEditor/DoTest/Dash';
import { colorsV2 } from '../colors-v2';
import { ResponsiveTypography } from '../Typography/v3/ResponsiveTypography';
import { DoTestEditor } from '../DoTestEditor/DoTestEditor';
import { FontSizeElementVariants } from '../DolEditor/decorators/font-size-switcher/components/FontSizeSwitcherElement.types';
import { isJson } from '@doltech/utils/lib/object';
import { isTouchDevice } from '@doltech/utils/lib/platform';
import { QuestionRadio } from './QuestionRadio';
import { flexGap } from '@doltech/utils/lib/css-style';
import { QuestionCheckbox } from './QuestionCheckbox';

export const QuestionDisplayMode = {
  QUESTION_NO: 'question-no',
  QUESTION_OPTION: 'question-option',
  QUESTION_OPTION_DASHED: 'question-option-dashed',
  QUESTION_OPTION_BASIC: 'question-option-basic',
  QUESTION_OPTION_SELECTED: 'question-option-selected',
  QUESTION_OPTION_CORRECT: 'question-option-correct',
  QUESTION_OPTION_CORRECT_MISSING: 'question-option-correct-missing',
  QUESTION_OPTION_WRONG: 'question-option-wrong',
  QUESTION_OPTION_MISSING: 'question-option-missing',
  QUESTION_OPTION_FADE: 'question-option-fade',
  QUESTION_OPTION_DRAG: 'question-option-drag',
  QUESTION_OPTION_DRAG_NOT_ANSWERED: 'question-option-drag-not-answered',
  QUESTION_OPTION_ANSWERED: 'question-option-answered',
  QUESTION_NO_VIEW_SUBJECT: 'question-no-view-subject',
  QUESTION_OPTION_VIEW_SUBJECT: 'question-option-view-subject',
  QUESTION_DO_TEST: 'question-do-test',
  QUESTION_RADIO: 'question-radio',
  QUESTION_CHECKBOX: 'question-checkbox',
  QUESTION_CHECKBOX_CORRECT: 'question-checkbox-correct',
  QUESTION_CHECKBOX_CORRECT_MISSING: 'question-checkbox-correct-missing',
  QUESTION_CHECKBOX_WRONG: 'question-checkbox-wrong',
} as const;

export type QuestionDisplayMode = (typeof QuestionDisplayMode)[keyof typeof QuestionDisplayMode];

export const QuestionCircleType = {
  GRAY_20: 'gray-20',
  GRAY_40: 'gray-40',
  GRAY_100: 'gray-100',
} as const;

export type QuestionCircleType = (typeof QuestionCircleType)[keyof typeof QuestionCircleType];

interface CircleTextProps extends React.HTMLAttributes<HTMLDivElement> {
  displayMode: QuestionDisplayMode;
  circleText: string | number;
  text: string;
  active?: boolean; // when user click or drag to.
  selected?: boolean;
  shape?: 'rounded-rectangle' | 'circle';
  shapeWidth?: number;
  useBlankDash?: boolean;
  note?: string;
  onSetNote?: (note: string) => void;
}

const Main = styled.span.withConfig({
  componentId: 'CircleText_Main',
} as any)`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 12px;
  .question-circle {
    margin-right: 12px;
  }
  .text-missed {
    color: ${colorsV2.yellow100};
  }

  &.selected {
    background-color: ${colorsV2.blue5};
  }

  &.question-option-wrapper {
    /* &:hover {
      background: ${colorsV2.gray10};
    }
    &.selected:hover {
      background: ${colorsV2.blue5};
      .question-circle.question-option {
        background: ${colorsV2.blue100};
      }
    } */
  }

  @media ${deviceBreakpoints.allExceptMobile} {
    .${QuestionDisplayMode.QUESTION_NO}.question-text {
      .reading-text {
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
      }
    }
  }
`;

const QuestionContainer = styled.div.withConfig({
  componentId: 'CircleText_QuestionContainer',
} as any)`
  flex: 1;
  display: flex;
  align-items: center;

  &.${QuestionDisplayMode.QUESTION_RADIO},
    &.${QuestionDisplayMode.QUESTION_CHECKBOX},
    &.${QuestionDisplayMode.QUESTION_CHECKBOX_CORRECT},
    &.${QuestionDisplayMode.QUESTION_CHECKBOX_CORRECT_MISSING},
    &.${QuestionDisplayMode.QUESTION_CHECKBOX_WRONG} {
    ${flexGap(12)};
    padding: 2px 0;
  }
`;

export const QuestionCircle = styled.div.withConfig({
  componentId: 'CircleText_QuestionCircle',
} as any)<{ shapeWidth?: number }>`
  ${(p) =>
    p.shapeWidth
      ? `width:${p.shapeWidth}px; min-width: ${p.shapeWidth}px; height: ${p.shapeWidth}px;`
      : 'width: 24px; min-width: 24px; height: 24px;'};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &.vertical-rectangle {
    height: 44px;

    .vertical-text {
      line-height: 10px;
      white-space: pre-wrap;
      text-align: center;
    }
  }

  > span {
    font-size: 10px;
    font-weight: 600;
    line-height: 16px;
  }
  &.shape-circle {
    border-radius: 12px;
  }
  &.${QuestionDisplayMode.QUESTION_NO} {
    background: ${colorsV2.gray10};
    border-radius: 12px;
    > span {
      white-space: nowrap;
      color: ${colorsV2.black80};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION} {
    font-weight: 600;
    background-color: ${colorsV2.gray20};
    > span {
      color: ${colorsV2.black100};
    }
    &.selected {
      background: ${colorsV2.blue100};
      > span {
        color: ${colorsV2.white100};
      }
    }
    &.bordered {
      border: 1px dashed ${colorsV2.gray60};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_DASHED} {
    background-color: ${colorsV2.gray10};
    border: 1px dashed ${colorsV2.gray60};
    > span {
      color: ${colorsV2.black100};
    }
    &.active {
      background: ${colorsV2.blue20};
      border: 1px dashed ${colorsV2.blue40};
    }
    &.selected {
      background: ${colorsV2.blue100};
      border: unset;
      > span {
        color: ${colorsV2.white100};
      }
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_ANSWERED} {
    background-color: ${colorsV2.blue20};
    > span {
      color: ${colorsV2.blue100};
    }
    &.selected {
      background: ${colorsV2.blue100};
      > span {
        color: ${colorsV2.white100};
      }
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_BASIC} {
    background-color: ${colorsV2.gray20};
    > span {
      color: ${colorsV2.black80};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_CORRECT},
    &.${QuestionDisplayMode.QUESTION_OPTION_CORRECT_MISSING} {
    background: ${colorsV2.green100};
    > span {
      color: ${colorsV2.white100};
    }
    &.light {
      background: ${colorsV2.green20};
      > span {
        color: ${colorsV2.green100};
      }
    }
    &.selected {
      background: ${colorsV2.green100};
      > span {
        color: ${colorsV2.white100};
      }
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_WRONG} {
    background: ${colorsV2.red100};
    > span {
      color: ${colorsV2.white100};
    }
    &.light {
      background: ${colorsV2.red20};
      > span {
        color: ${colorsV2.red100};
      }
    }
    &.selected {
      background: ${colorsV2.red100};
      > span {
        color: ${colorsV2.white100};
      }
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_FADE} {
    background: ${colorsV2.gray10};
    > span {
      color: ${colorsV2.black80};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_MISSING} {
    background: ${colorsV2.yellow100};
    > span {
      color: ${colorsV2.white100};
    }
    &.light {
      background: ${colorsV2.yellow20};
      > span {
        color: ${colorsV2.yellow100};
      }
    }
    &.selected {
      background: ${colorsV2.yellow100};
      > span {
        color: ${colorsV2.white100};
      }
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_DRAG_NOT_ANSWERED} {
    background: ${colorsV2.blue20};
    border: 1px dashed ${colorsV2.blue40};
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_DRAG} {
    background: ${colorsV2.blue100};
    > span {
      color: ${colorsV2.white100};
    }
  }
  &.${QuestionDisplayMode.QUESTION_NO_VIEW_SUBJECT} {
    background: ${colorsV2.gray100};
    &.question-circle {
      margin-right: 16px;
    }
    > span {
      color: ${colorsV2.white100};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_VIEW_SUBJECT} {
    background-color: ${colorsV2.gray20};
    user-select: none;
    > span {
      color: ${colorsV2.black100};
    }
  }
  &.${QuestionDisplayMode.QUESTION_DO_TEST} {
    background-color: ${colorsV2.black80};
    user-select: none;
    border-radius: 50%;
    > span {
      white-space: nowrap;
      color: ${colorsV2.white100};
      font-weight: 600;
    }
    &.selected {
      background: ${colorsV2.blue100};
      > span {
        color: ${colorsV2.white100};
      }
    }
  }
  &.${QuestionCircleType.GRAY_20} {
    background: ${colorsV2.gray20};
    > span {
      color: ${colorsV2.black100};
    }
  }
  &.${QuestionCircleType.GRAY_40} {
    background: ${colorsV2.gray40};
    > span {
      color: ${colorsV2.black100};
    }
  }
  &.${QuestionCircleType.GRAY_100} {
    background: ${colorsV2.gray100};
    > span {
      color: ${colorsV2.white100};
    }
  }

  &.shape-rounded-rectangle {
    border-radius: 12px;
  }
`;

export const QuestionText = styled.div.withConfig({
  componentId: 'CircleText_QuestionText',
} as any)`
  display: flex;
  &.${QuestionDisplayMode.QUESTION_OPTION}, &.${QuestionDisplayMode.QUESTION_OPTION_SELECTED} {
    > span {
      font-weight: 400;
      font-size: 14px;
      color: ${colorsV2.black100};

      @media ${deviceBreakpoints.allExceptMobile} {
        font-weight: 400;
      }
    }
  }
  &.${QuestionDisplayMode.QUESTION_NO},
    &.${QuestionDisplayMode.QUESTION_OPTION_CORRECT},
    &.${QuestionDisplayMode.QUESTION_OPTION_CORRECT_MISSING} {
    > span {
      color: ${colorsV2.black100};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_WRONG} {
    > span {
      color: ${colorsV2.black100};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_FADE} {
    > span {
      color: ${colorsV2.gray100};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_DRAG} {
    > span {
      color: ${colorsV2.black100};
    }
  }
  &.${QuestionDisplayMode.QUESTION_NO_VIEW_SUBJECT} {
    > span {
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      color: ${colorsV2.black100};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_VIEW_SUBJECT} {
    user-select: none;
    > span {
      font-size: 16px;
      line-height: 24px;
      color: ${colorsV2.black100};
    }
  }
  &.${QuestionDisplayMode.QUESTION_DO_TEST} {
    user-select: none;
    > span {
      line-height: 20px;
      font-weight: 600;
      font-size: 14px;
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_DASHED} {
    > span {
      color: ${colorsV2.gray80};
      font-weight: 400;
    }
    &.active {
      > span {
        color: ${colorsV2.blue40};
      }
    }
    &.selected {
      > span {
        color: ${colorsV2.blue100};
      }
    }
  }

  .reading-text {
    white-space: pre-wrap;
    line-height: 20px;
    font-size: 14px;
  }
`;

const DASH = '-';

export const CircleText = (props: CircleTextProps) => {
  const {
    displayMode,
    circleText,
    text,
    active,
    selected,
    shape = 'circle',
    useBlankDash,
    note,
    onSetNote,
    ...rest
  } = props;

  let classVerticalRectangle = '';
  let finalCircleText: string = circleText?.toString();
  if (finalCircleText?.includes(DASH)) {
    classVerticalRectangle = 'vertical-rectangle';
    const circleTextParts = finalCircleText?.split(DASH);
    if (circleTextParts.length === 2) {
      finalCircleText = circleTextParts[0]
        .concat('\n')
        .concat(DASH)
        .concat('\n')
        .concat(circleTextParts[1]);
    }
  }

  const renderQuestionCircle = () => {
    switch (displayMode) {
      case QuestionDisplayMode.QUESTION_RADIO:
        return <QuestionRadio selected={selected} />;

      case QuestionDisplayMode.QUESTION_CHECKBOX:
        return <QuestionCheckbox selected={selected} />;

      case QuestionDisplayMode.QUESTION_CHECKBOX_CORRECT:
        return <QuestionCheckbox state="correct" />;

      case QuestionDisplayMode.QUESTION_CHECKBOX_CORRECT_MISSING:
        return <QuestionCheckbox state="missed" />;

      case QuestionDisplayMode.QUESTION_CHECKBOX_WRONG:
        return <QuestionCheckbox state="wrong" />;

      default:
        return (
          <QuestionCircle
            className={cl(
              'question-circle',
              displayMode,
              { active, selected, [`shape-${shape}`]: true },
              classVerticalRectangle
            )}
          >
            <ResponsiveTypography.Text className="vertical-text" variant="semibold/12-20">
              {finalCircleText}
            </ResponsiveTypography.Text>
          </QuestionCircle>
        );
    }
  };

  return (
    <Main className="circle-text" {...rest}>
      <QuestionContainer
        className={cl('question-circle-text-container', displayMode, {
          selected,
          [`shape-${shape}`]: true,
        })}
      >
        {renderQuestionCircle()}
        {useBlankDash && <Dash className="dash" />}
        <QuestionText
          className={cl('question-text', displayMode, { active, selected })}
          onClick={(e) => {
            if (!isTouchDevice()) {
              e.stopPropagation();
            }
          }}
        >
          <ResponsiveTypography.Text variant="regular/14">
            <DoTestEditor
              readOnly
              initialValue={
                isJson(note)
                  ? JSON.parse(note)
                  : [
                      {
                        type: FontSizeElementVariants.PARAGRAPH,
                        children: [
                          {
                            text: text ?? '',
                          },
                        ],
                      },
                    ]
              }
              onChange={onSetNote}
            />
          </ResponsiveTypography.Text>
        </QuestionText>
      </QuestionContainer>
      {displayMode === QuestionDisplayMode.QUESTION_CHECKBOX_CORRECT_MISSING && (
        <ResponsiveTypography.Text variant="regular/14" color={colorsV2.neutralSecondary}>
          Bỏ trống
        </ResponsiveTypography.Text>
      )}
    </Main>
  );
};
